import styled from "styled-components";
import {ChevronRight, StarFullIcon} from "ui/icons/index";
import {defaultThemeColors, fonts, mediaSizes, themeFontSize} from "ui/theme";
import {isBasedInNYC} from "utils/utils.ts";
import React from "react";

const StarRating = ({rating}) => {
    const stars = Array(5).fill(null);

    return (
        <RatingContainer>
            <span role="stars" aria-label="multi-star">
                {stars.map((_, i) => (
                    <StarFullIcon key={i} size={10}/>
                ))}
            </span>
            <RatingValue>{rating.toFixed(1)}</RatingValue>
        </RatingContainer>
    );
};

const Breadcrumbs = ({classType, classSubCategory, name}) => {
    if (!classType) return null;

    return (
        <BreadcrumbsContainer>
            <a href="/" className="title">Home</a>
            <ChevronRight/>
            <p className="title">{classType}</p>

            {classSubCategory && name && (
                <>
                    <ChevronRight/>
                    <p className="class-sub-category">
                        {classSubCategory} with {name}
                    </p>
                </>
            )}
        </BreadcrumbsContainer>
    );
};

const MobileBadges = ({
                          fiveStarsReviewsCount,
                          isAllLevels,
                          isBasedInNYCFlag,
                          city,
                          avgRating,
                          isTopInstructor,
                          isDiscountOffered,
                      }) => {
    const displayCity =
        city?.toLowerCase().trim() === "new york" ? "NYC" : city;

    const badges = [
        fiveStarsReviewsCount > 0 && (
            <>
                <span role="stars">✨</span>
                <ReviewCount>{fiveStarsReviewsCount} five-star reviews</ReviewCount>
            </>
        ),
        isAllLevels && <AllLevels>All Levels</AllLevels>,
        isBasedInNYCFlag && (
            <Location>Based in {displayCity}</Location>
        ),
    ].filter(Boolean);

    return (
        <BadgesContainer>
            <FirstLine>
                {badges.map((badge, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && <span className="dot">&bull;</span>}
                        {badge}
                    </React.Fragment>
                ))}
            </FirstLine>
            <SecondLine>
                {avgRating > 0 && <StarRating rating={avgRating}/>}
                {isTopInstructor && <Badge>🏆 Top Instructor</Badge>}
                {isDiscountOffered && <DiscountBadge>🎉 Discount Offered</DiscountBadge>}
            </SecondLine>
        </BadgesContainer>
    );
};

export const HeaderSection = ({classDetail, reviews}) => {
    const teacher = classDetail?.teacher;

    const classType = teacher?.class?.title || "";
    const classSubCategory = teacher?.subject_sub_category || "";
    const name = classDetail?.first_name || "";

    const avgRating = teacher?.rate || 0;
    const isAllLevels = teacher?.level_of_student?.split(",").length >= 3;
    const isBasedInNYCFlag = isBasedInNYC(teacher?.city, teacher?.state);
    const isTopInstructor = teacher?.badge?.toLowerCase() === "top";
    const isDiscountOffered = teacher?.teacher_discount_policy?.length > 0;
    const fiveStarsReviewsCount = reviews?.filter((r) => r.rating >= 5)?.length || 0;

    return (
        <HeaderContainer>
            <MobileBadges
                fiveStarsReviewsCount={fiveStarsReviewsCount}
                isAllLevels={isAllLevels}
                isBasedInNYCFlag={isBasedInNYCFlag}
                city={teacher?.city}
                avgRating={avgRating}
                isTopInstructor={isTopInstructor}
                isDiscountOffered={isDiscountOffered}
            />

            <Breadcrumbs
                classType={classType}
                classSubCategory={classSubCategory}
                name={name}
            />
        </HeaderContainer>
    );
};

const HeaderContainer = styled.div`
    font-family: Montserrat;
`;

const BreadcrumbsContainer = styled.div`
    display: none;
    align-items: center;
    gap: 1rem;
    padding-top: 1rem;

    @media (min-width: ${mediaSizes.md}) {
        display: flex;
    }

    & .title {
        font-weight: 600;
        font-size: ${themeFontSize("regular")};
    }

    & .class-sub-category {
        font-weight: 400;
        font-size: ${themeFontSize("regular")};
    }
`;

const BadgesContainer = styled.div`
    display: grid;
    gap: 0.5rem;
    margin-bottom: 20px;
    padding-top: 1rem;

    @media (min-width: ${mediaSizes.md}) {
        display: none;
    }
`;

const FirstLine = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: ${defaultThemeColors.grey};
    font-size: ${fonts.sizes.extraSmall};
    font-weight: 500;

    & .dot {
        font-weight: 700;
    }

    & span[role="stars"] {
        margin-top: -1.5px;
    }
`;

const ReviewCount = styled.span`
    white-space: nowrap;
    margin-left: -5px;
`;

const AllLevels = styled.span`
    white-space: nowrap;
`;

const Location = styled.span`
    white-space: nowrap;
`;

const SecondLine = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-weight: 500;
`;

const RatingContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    background-color: ${defaultThemeColors.lightBlue};
    height: 24px;
    & span[role="stars"] {
        display: flex;
        gap: 2px;
        color: #fb9902;
    }
`;

const RatingValue = styled.span`
    font-size: ${fonts.sizes.extraSmall};
    color: ${defaultThemeColors.txt};
    font-weight: 500;
`;

const Badge = styled.div`
    padding: 4px 10px;
    border-radius: 4px;
    background-color: ${defaultThemeColors.lightBlue};
    font-size: ${fonts.sizes.extraSmall};
    font-weight: 500;
    white-space: nowrap;
`;

const DiscountBadge = styled.div`
    padding: 4px 10px;
    border-radius: 4px;
    background-color: ${defaultThemeColors.lightBlue};
    font-size: ${fonts.sizes.extraSmall};
    font-weight: 500;
    white-space: nowrap;
`;
